import { useCallback, useState } from 'react';
import { api } from '~/api/client';
import type { ReviewSort } from '~/api/types';
import {
  Badge,
  Button,
  EmptyState,
  ErrorPanel,
  Spinner,
  Stars,
  Select,
  classes,
  formatRelative,
} from '~/shared/ui';
import { useMutation, useQuery } from '~/shared/hooks/useQuery';
import { Link } from 'react-router-dom';
import { useSession } from '~/features/account/SessionProvider';

const SORTS: readonly { value: ReviewSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'highest', label: 'Highest rated' },
  { value: 'lowest', label: 'Lowest rated' },
];

/**
 * Reviews and the form to write one (§44).
 *
 * The verified-installation badge is rendered from the API's own flag and there is no way for the
 * form to influence it — the platform decides whether the author actually installed the application,
 * which is the whole point of the label.
 */
export function ReviewsPanel({
  slug,
  onSubmitted,
}: {
  slug: string;
  onSubmitted: () => void;
}): JSX.Element {
  const [sort, setSort] = useState<ReviewSort>('newest');
  const reviews = useQuery((signal) => api.reviews(slug, { sort }, signal), [slug, sort]);

  const handleSubmitted = useCallback(() => {
    reviews.reload();
    onSubmitted();
  }, [reviews, onSubmitted]);

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_20rem]">
      <div>
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-semibold text-slate-100">Reviews</h2>
          <Select
            aria-label="Sort reviews"
            className="w-44"
            value={sort}
            options={SORTS}
            onChange={(value) => setSort(value as ReviewSort)}
          />
        </div>

        {reviews.error !== undefined ? (
          <ErrorPanel error={reviews.error} onRetry={reviews.reload} />
        ) : reviews.data === undefined ? (
          <div className="grid place-items-center py-12 text-slate-500">
            <Spinner className="h-5 w-5" />
          </div>
        ) : reviews.data.items.length === 0 ? (
          <EmptyState
            title="No reviews yet"
            description="Nobody has written about this application. If you have installed it, your experience would help the next person."
          />
        ) : (
          <ul className={classes('space-y-4', reviews.isRefreshing && 'opacity-60')}>
            {reviews.data.items.map((review) => (
              <li key={review.id} className="card">
                <div className="flex flex-wrap items-center gap-3">
                  <Stars average={review.rating} count={0} />
                  <span className="font-medium text-slate-100">{review.title}</span>
                  {review.fromVerifiedInstallation ? (
                    <Badge tone="good" title="The marketplace confirmed this reviewer installed the application">
                      Verified install
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">{review.body}</p>
                <p className="mt-2 text-xs text-slate-500">{formatRelative(review.createdAt)}</p>

                {review.developerResponse === undefined ? null : (
                  <div className="mt-3 rounded-lg border-l-2 border-brand/50 bg-slate-950/50 p-3">
                    <p className="text-xs font-medium text-brand-soft">Developer response</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-300">
                      {review.developerResponse.body}
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ReviewForm slug={slug} onSubmitted={handleSubmitted} />
    </div>
  );
}

function ReviewForm({
  slug,
  onSubmitted,
}: {
  slug: string;
  onSubmitted: () => void;
}): JSX.Element {
  const { isSignedIn } = useSession();
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [wasRevision, setWasRevision] = useState<boolean | undefined>(undefined);

  const submit = useMutation((input: { rating: number; title: string; body: string }) =>
    api.submitReview(slug, input),
  );

  if (!isSignedIn) {
    return (
      <aside className="card h-max">
        <h3 className="font-semibold text-slate-100">Write a review</h3>
        <p className="mt-2 text-sm text-slate-400">
          Sign in to share how this application worked for you.
        </p>
        <Link to={`/signin?returnTo=${encodeURIComponent(`/applications/${slug}`)}`} className="mt-4 block">
          <Button variant="secondary" className="w-full">
            Sign in
          </Button>
        </Link>
      </aside>
    );
  }

  if (wasRevision !== undefined) {
    return (
      <aside className="card h-max">
        <h3 className="font-semibold text-slate-100">Thanks</h3>
        <p className="mt-2 text-sm text-slate-400">
          {wasRevision ? 'Your review has been updated.' : 'Your review has been published.'}
        </p>
      </aside>
    );
  }

  return (
    <aside className="card h-max">
      <h3 className="font-semibold text-slate-100">Write a review</h3>
      <form
        className="mt-4 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit.run({ rating, title, body }).then((outcome) => {
            if (outcome.ok) {
              setWasRevision(outcome.data.wasRevision);
              onSubmitted();
            }
          });
        }}
      >
        <fieldset>
          <legend className="label">Rating</legend>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                aria-label={`${String(value)} star${value === 1 ? '' : 's'}`}
                aria-pressed={rating === value}
                onClick={() => setRating(value)}
                className={classes(
                  'text-2xl leading-none transition-colors',
                  value <= rating ? 'text-amber-400' : 'text-slate-700 hover:text-slate-500',
                )}
              >
                ★
              </button>
            ))}
          </div>
        </fieldset>

        <div>
          <label className="label" htmlFor="review-title">
            Summary
          </label>
          <input
            id="review-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={100}
            required
            className="input"
            placeholder="Worked exactly as described"
          />
          {submit.error?.fields['title'] === undefined ? null : (
            <p className="hint text-red-300">{submit.error.fields['title']}</p>
          )}
        </div>

        <div>
          <label className="label" htmlFor="review-body">
            Your experience
          </label>
          <textarea
            id="review-body"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={5}
            minLength={10}
            maxLength={4000}
            required
            className="input resize-y"
            placeholder="What did you use it for, and how did the installation go?"
          />
          <p className="hint">
            {body.trim().length < 10
              ? 'At least 10 characters.'
              : `${String(4000 - body.length)} characters left.`}
          </p>
          {submit.error?.fields['body'] === undefined ? null : (
            <p className="hint text-red-300">{submit.error.fields['body']}</p>
          )}
        </div>

        {submit.error !== undefined && Object.keys(submit.error.fields).length === 0 ? (
          <p role="alert" className="text-sm text-red-300">
            {submit.error.message}
          </p>
        ) : null}

        <Button type="submit" variant="primary" isLoading={submit.isRunning} className="w-full">
          Publish review
        </Button>
      </form>
    </aside>
  );
}
